import {
  AnimationConfigWithData,
  AnimationConfigWithPath,
  AnimationItem,
  LottiePlayer,
} from 'lottie-web/build/player/lottie_light';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import LottieController from '../../lazyLibraries/LottieController';

interface LottieProps {
  animation: Partial<AnimationConfigWithPath<'svg'> | AnimationConfigWithData<'svg'>>;
  fallback: ReactNode;
  canPlay: boolean;
  className: string;
  onComplete?: () => void;
  complete: boolean;
}

export const Lottie: FunctionComponent<LottieProps> = ({
  animation,
  canPlay,
  className,
  onComplete,
  fallback,
  complete,
}) => {
  // Animation container ref
  const animationRef = useRef<HTMLDivElement>(null);
  // Animation ref
  const lottieRef = useRef<AnimationItem>();
  // Animation complete boolean value
  const [completeInternal, setCompleteInternal] = useState(false);

  const [lottie, setLottie] = useState<LottiePlayer>();

  // Set complete initially from props
  useEffect(() => {
    setCompleteInternal(complete);
  }, [complete]);

  useEffect(() => {
    LottieController.addListener('lobbyAnimation', (lottie) => {
      setLottie(lottie);
    });
  });

  // fire callback onComplete function when animation completed and update internal state
  const handleComplete = useCallback((): void => {
    setCompleteInternal(true);
    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    if (animationRef.current) {
      lottieRef.current = lottie?.loadAnimation({
        ...animation,
        container: animationRef.current, // the dom element that will contain the animation
      });
    }
  }, [animation, lottie]);

  useEffect(() => {
    // add complete listener to animation
    lottieRef.current?.addEventListener('complete', () => {
      handleComplete();
    });
  }, [handleComplete]);

  useEffect(() => {
    // play if animation loaded
    if (canPlay && lottieRef.current && !completeInternal) {
      lottieRef.current.play();
    }
    // If fallback rendered make animation complete to not play this animation in future
    if (!lottie && canPlay) {
      handleComplete();
    }
  }, [canPlay, completeInternal, handleComplete, lottie]);

  // conditionally render animation or fallback
  return lottie && !completeInternal ? <div ref={animationRef} className={className} /> : fallback;
};
